<script setup>
import { computed, nextTick, onBeforeUnmount, ref } from "vue"
import { DirectUpload } from "@rails/activestorage"
import imageCompression from "browser-image-compression"
import Cropper from "cropperjs"
import { AnnotationEditor } from "@markerjs/markerjs-ui"
import { ArrowMarker, EllipseMarker, FrameMarker, TextMarker } from "@markerjs/markerjs3"

const props = defineProps({
  inputName: { type: String, required: true },
  inputId: { type: String, required: true },
  defaultText: { type: String, default: "Drop a file here or click to upload" },
  accept: { type: String, default: "image/*" },
  capture: { type: String, default: "" },
  directUploadUrl: { type: String, required: true },
})

const emit = defineEmits(["fileChange", "previewReady", "uploadComplete", "uploadError"])

const isDragging = ref(false)
const statusText = ref(props.defaultText)
const uploading = ref(false)
const uploadProgress = ref(0)
const signedId = ref("")
const uploadError = ref("")
const workflowStep = ref(1)
const isCropping = ref(false)
const isAnnotating = ref(false)
const cropSourceUrl = ref("")
const annotationSourceUrl = ref("")
const croppedFileForAnnotation = ref(null)
const annotatedFile = ref(null)
const selectedFile = ref(null)
const activeAspect = ref("free")
let dragDepth = 0

const workflowOpen = computed(() => isCropping.value || isAnnotating.value || uploading.value)

const fileInput = ref(null)
const cropContainer = ref(null)
const cropImage = ref(null)
const annotationStage = ref(null)
const annotationImageWrapper = ref(null)
const annotationImage = ref(null)
const annotationEditorHost = ref(null)
const annotationEditorInstance = ref(null)
let cropperInstance = null

const aspectRatios = {
  free: Number.NaN,
  square: 1,
  landscape: 4 / 3,
  wide: 16 / 9,
}

function destroyCropper() {
  if (cropperInstance) {
    cropperInstance.destroy()
    cropperInstance = null
  }
}

function revokeCropSourceUrl() {
  if (cropSourceUrl.value) {
    URL.revokeObjectURL(cropSourceUrl.value)
    cropSourceUrl.value = ""
  }
}

function revokeAnnotationSourceUrl() {
  if (annotationSourceUrl.value) {
    URL.revokeObjectURL(annotationSourceUrl.value)
    annotationSourceUrl.value = ""
  }
}

function destroyAnnotationEditor() {
  if (annotationEditorHost.value) {
    annotationEditorHost.value.replaceChildren()
    annotationEditorHost.value.style.display = "none"
  }
  annotationEditorInstance.value = null
}

function resetAnnotationState() {
  destroyAnnotationEditor()
  revokeAnnotationSourceUrl()
  isAnnotating.value = false
  croppedFileForAnnotation.value = null
  annotatedFile.value = null
}

function resetCropState() {
  destroyCropper()
  revokeCropSourceUrl()
  selectedFile.value = null
  isCropping.value = false
  activeAspect.value = "free"
}

onBeforeUnmount(() => {
  destroyCropper()
  revokeCropSourceUrl()
  destroyAnnotationEditor()
  revokeAnnotationSourceUrl()
})

function onDragEnter(event) {
  event.preventDefault()
  dragDepth += 1
  isDragging.value = true
}

function onDragOver(event) {
  event.preventDefault()
}

function onDragLeave(event) {
  event.preventDefault()
  dragDepth -= 1
  if (dragDepth <= 0) {
    dragDepth = 0
    isDragging.value = false
  }
}

function onDrop(event) {
  event.preventDefault()
  dragDepth = 0
  isDragging.value = false

  const files = event.dataTransfer?.files
  if (files?.length > 0) {
    fileInput.value.files = files
    handleFileInputChange()
  }
}

function handleFileInputChange() {
  const file = fileInput.value?.files?.[0]
  if (!file) {
    statusText.value = props.defaultText
    return
  }

  prepareCropFlow(file)
}

async function prepareCropFlow(file) {
  resetCropState()
  resetAnnotationState()

  uploadError.value = ""
  signedId.value = ""
  uploadProgress.value = 0
  workflowStep.value = 2
  isCropping.value = true
  selectedFile.value = file
  statusText.value = `Selected: ${file.name}`
  emit("fileChange", file)

  cropSourceUrl.value = URL.createObjectURL(file)
  await nextTick()

  if (!cropImage.value || !cropContainer.value) {
    uploadError.value = "Could not initialize cropper. Please try again."
    isCropping.value = false
    workflowStep.value = 1
    return
  }

  cropperInstance = new Cropper(cropImage.value, {
    container: cropContainer.value
  })
  await nextTick()
  setAspectRatio("free")
}

function fitSelectionToRenderedImage() {
  const selection = cropperInstance?.getCropperSelection?.()
  const image = cropperInstance?.getCropperImage?.()
  const canvas = cropperInstance?.getCropperCanvas?.()
  if (!selection || !image || !canvas) return

  const imageRect = image.getBoundingClientRect()
  const canvasRect = canvas.getBoundingClientRect()

  const width = Math.max(1, Math.min(imageRect.width, canvasRect.width))
  const height = Math.max(1, Math.min(imageRect.height, canvasRect.height))
  const x = imageRect.left - canvasRect.left
  const y = imageRect.top - canvasRect.top

  selection.$change(x, y, width, height)
}

function fitSelectionToRenderedImageWithAspectRatio(aspectRatio) {
  const selection = cropperInstance?.getCropperSelection?.()
  const image = cropperInstance?.getCropperImage?.()
  const canvas = cropperInstance?.getCropperCanvas?.()
  if (!selection || !image || !canvas || !Number.isFinite(aspectRatio) || aspectRatio <= 0) return

  const imageRect = image.getBoundingClientRect()
  const canvasRect = canvas.getBoundingClientRect()
  if (!imageRect.width || !imageRect.height) return

  // Build the largest crop box for this ratio that still fits entirely inside the image.
  let width = imageRect.width
  let height = width / aspectRatio

  if (height > imageRect.height) {
    height = imageRect.height
    width = height * aspectRatio
  }

  const x = imageRect.left - canvasRect.left + (imageRect.width - width) / 2
  const y = imageRect.top - canvasRect.top + (imageRect.height - height) / 2

  selection.$change(x, y, width, height)
}

function buildExportCanvasOptions() {
  const selection = cropperInstance?.getCropperSelection?.()
  const image = cropperInstance?.getCropperImage?.()
  if (!selection || !image?.$image) return undefined

  const imageRect = image.getBoundingClientRect()
  const naturalWidth = image.$image.naturalWidth || 0
  const naturalHeight = image.$image.naturalHeight || 0
  if (!imageRect.width || !imageRect.height || !naturalWidth || !naturalHeight) return undefined

  // Map selection size from rendered crop box pixels back to source image pixels.
  const scaleX = naturalWidth / imageRect.width
  const scaleY = naturalHeight / imageRect.height

  return {
    width: Math.max(1, Math.round(selection.width * scaleX)),
    height: Math.max(1, Math.round(selection.height * scaleY)),
  }
}

function setAspectRatio(aspectKey) {
  activeAspect.value = aspectKey
  const selection = cropperInstance?.getCropperSelection?.()
  if (!selection) return

  const ratio = aspectRatios[aspectKey]
  const isFree = aspectKey === "free"

  // In free mode, start with the full rendered image selected.
  selection.initialCoverage = isFree ? 1 : 0.85
  selection.aspectRatio = ratio
  selection.initialAspectRatio = ratio
  selection.$reset()

  if (isFree) {
    const image = cropperInstance?.getCropperImage?.()
    if (image?.$ready) {
      image.$ready(() => {
        requestAnimationFrame(() => {
          fitSelectionToRenderedImage()
        })
      })
    } else {
      requestAnimationFrame(() => {
        fitSelectionToRenderedImage()
      })
    }
    return
  }

  const image = cropperInstance?.getCropperImage?.()
  if (image?.$ready) {
    image.$ready(() => {
      requestAnimationFrame(() => {
        fitSelectionToRenderedImageWithAspectRatio(ratio)
      })
    })
  } else {
    requestAnimationFrame(() => {
      fitSelectionToRenderedImageWithAspectRatio(ratio)
    })
  }
}

function cancelCrop() {
  resetCropState()
  resetAnnotationState()
  workflowStep.value = 1
  statusText.value = props.defaultText
  uploadError.value = ""
  uploadProgress.value = 0

  if (fileInput.value) {
    fileInput.value.value = ""
  }
}

async function canvasToFile(canvas, sourceFileName, sourceFileType) {
  const fallbackType = sourceFileType?.startsWith("image/") ? sourceFileType : "image/jpeg"
  const blob = await new Promise((resolve, reject) => {
    canvas.toBlob((value) => {
      if (!value) {
        reject(new Error("Failed to create cropped image."))
        return
      }
      resolve(value)
    }, fallbackType, 0.92)
  })

  return new File([blob], sourceFileName, {
    type: blob.type || fallbackType,
    lastModified: Date.now(),
  })
}

async function dataUrlToFile(dataUrl, sourceFileName) {
  const response = await fetch(dataUrl)
  const blob = await response.blob()

  return new File([blob], sourceFileName, {
    type: blob.type || "image/png",
    lastModified: Date.now(),
  })
}

async function buildCroppedFile() {
  const selection = cropperInstance?.getCropperSelection?.()
  if (!selection || !selectedFile.value) {
    throw new Error("No image available to crop.")
  }

  const exportOptions = buildExportCanvasOptions()
  const canvas = await selection.$toCanvas(exportOptions)
  return canvasToFile(canvas, selectedFile.value.name, selectedFile.value.type)
}

async function uploadSelectedFile(file, uploadStatusText) {
  workflowStep.value = 4
  uploading.value = true
  uploadProgress.value = 0
  uploadError.value = ""
  statusText.value = uploadStatusText

  emit("previewReady", URL.createObjectURL(file))
  emit("fileChange", file)

  let fileToUpload
  try {
    fileToUpload = await imageCompression(file, {
      maxWidthOrHeight: 1080,
      initialQuality: 0.8,
      useWebWorker: true,
    })
  } catch {
    fileToUpload = file
  }

  const upload = new DirectUpload(fileToUpload, props.directUploadUrl, {
    directUploadWillStoreFileWithXHR(xhr) {
      xhr.upload.addEventListener("progress", (event) => {
        if (event.lengthComputable) {
          uploadProgress.value = Math.round((event.loaded / event.total) * 100)
        }
      })
    },
  })

  await new Promise((resolve, reject) => {
    upload.create((error, blob) => {
      if (error) {
        reject(error)
        return
      }

      signedId.value = blob.signed_id
      emit("uploadComplete", blob.signed_id)
      resolve()
    })
  })

  statusText.value = `Uploaded: ${file.name}`
}

async function openMarkupEditor() {
  if (!annotationImage.value || !annotationSourceUrl.value || !annotationEditorHost.value) {
    return
  }

  destroyAnnotationEditor()

  // Wait for image to load with valid dimensions
  await new Promise((resolve) => {
    if (annotationImage.value.complete && annotationImage.value.naturalWidth > 0) {
      resolve()
    } else {
      annotationImage.value.onload = () => resolve()
    }
  })

  const annotationEditor = new AnnotationEditor()
  annotationEditor.targetImage = annotationImage.value
  
  // Auto-zoom to fit image in viewport
  annotationEditor.settings.autoZoomOut = true
  annotationEditor.settings.autoZoomIn = true

  // Mount the editor
  annotationEditorHost.value.replaceChildren(annotationEditor)
  annotationEditorHost.value.style.display = ""
  annotationEditorInstance.value = annotationEditor

  // Wait for web component to connect
  await nextTick()

  const markerArea = annotationEditor.markerArea
  if (!markerArea) return
  
  // Configure available markers and settings
  markerArea.availableMarkerTypes = [TextMarker, ArrowMarker, FrameMarker, EllipseMarker]
  
  if (markerArea.settings) {
    markerArea.settings.defaultStrokeWidths = [2, 5, 8]
    markerArea.settings.defaultStrokeWidth = 5
    markerArea.settings.defaultColorSet = [
      "#E63946",
      "#F4A261",
      "#F1FA8C",
      "#2A9D8F",
      "#457B9D",
      "#1D3557",
      "#FFFFFF",
      "#000000",
    ]
    markerArea.settings.defaultColor = "#E63946"
    markerArea.settings.defaultFillColor = "rgba(255,255,255,0.15)"
  }

  annotationEditor.addEventListener("editorsave", async (event) => {
    const fileName = croppedFileForAnnotation.value?.name || "annotated-image.png"
    if (!event.detail.dataUrl) return

    const markedUpFile = await dataUrlToFile(event.detail.dataUrl, fileName)

    revokeAnnotationSourceUrl()
    annotationSourceUrl.value = URL.createObjectURL(markedUpFile)
    annotatedFile.value = markedUpFile
    statusText.value = `Markup ready: ${markedUpFile.name}`
    destroyAnnotationEditor()
  })

  annotationEditor.addEventListener("editorclose", () => {
    destroyAnnotationEditor()
  })
}

async function cropAndAnnotate() {
  if (!selectedFile.value) return

  try {
    const croppedFile = await buildCroppedFile()
    destroyCropper()
    isCropping.value = false

    resetAnnotationState()
    workflowStep.value = 3
    isAnnotating.value = true
    croppedFileForAnnotation.value = croppedFile
    annotationSourceUrl.value = URL.createObjectURL(croppedFile)
    statusText.value = "Add markup (text, arrows, squares, circles), then upload."

    await nextTick()
    await openMarkupEditor()
  } catch (error) {
    uploadError.value = "Could not crop image. Please try again."
    workflowStep.value = 2
    emit("uploadError", error)
  }
}

async function finishWorkflow() {
  const fileToUpload = annotatedFile.value || croppedFileForAnnotation.value
  if (!fileToUpload) return

  const status = annotatedFile.value ? "Uploading marked up image..." : "Uploading cropped image..."

  try {
    await uploadSelectedFile(fileToUpload, status)
    resetCropState()
    resetAnnotationState()
  } catch (error) {
    uploadError.value = "Upload failed. Please try again."
    workflowStep.value = 3
    emit("uploadError", error)
  } finally {
    uploading.value = false
  }
}
</script>

<template>
  <div class="vstack gap-2">
    <!--
      Only submit the signed_id to Rails when a new upload has completed.
      Omitting the field entirely when empty tells Rails to leave the
      existing attachment untouched.
    -->
    <input v-if="signedId" type="hidden" :name="inputName" :value="signedId">

    <div
      class="step-image-dropzone"
      :class="{ 'is-dragging': isDragging }"
      @dragenter="onDragEnter"
      @dragover="onDragOver"
      @dragleave="onDragLeave"
      @drop="onDrop"
    >
      <!-- File input is for selection only — no name, not submitted directly -->
      <input
        ref="fileInput"
        type="file"
        class="form-control"
        :id="inputId"
        :accept="accept"
        :capture="capture || undefined"
        @change="handleFileInputChange"
      >

      <div class="step-image-dropzone__content">
        <p class="mb-1 fw-semibold">Drop an image here, click to upload, or take a photo</p>
        <p class="mb-0 text-muted small">{{ statusText }}</p>
      </div>
    </div>

    <div
      v-if="workflowOpen"
      class="modal fade show d-block"
      role="dialog"
      aria-modal="true"
      data-bs-backdrop="static"
      aria-label="Image crop workflow"
      tabindex="-1"
    >
      <div class="modal-dialog modal-fullscreen">
        <div class="modal-content">
          <div class="modal-header">
            <div class="w-100">
              <div class="step-image-dropzone__steps justify-content-center d-flex gap-2">
                <span class="step-image-dropzone__step" :class="{ 'is-active': workflowStep === 1 }">1. Select</span>
                <span class="step-image-dropzone__step" :class="{ 'is-active': workflowStep === 2 }">2. Crop</span>
                <span class="step-image-dropzone__step" :class="{ 'is-active': workflowStep === 3 }">3. Markup</span>
                <span class="step-image-dropzone__step" :class="{ 'is-active': workflowStep === 4 }">4. Upload</span>
              </div>
            </div>
          </div>

          <div class="modal-body d-flex flex-column">
            <div 
              v-if="isCropping" 
              ref="cropContainer" 
              class="col step-image-cropper__canvas d-flex flex-column align-items-center"
            >
              <img ref="cropImage" :src="cropSourceUrl" alt="Crop preview">
            </div>

            <div
              v-if="isAnnotating"
              ref="annotationStage"
              class="d-flex flex-column flex-grow-1 position-relative w-100 h-100"
            >
              <!-- Target image for AnnotationEditor -->
              <img
                ref="annotationImage"
                :src="annotationSourceUrl"
                alt="Markup preview"
                style="display: none;"
                crossorigin="anonymous"
              >
              <!-- Editor container - clean container for web component -->
              <div ref="annotationEditorHost" class="w-100 h-100" style="position: relative; display: none;"></div>
              <!-- Image preview - shown when not editing -->
              <div v-if="!annotationEditorInstance" class="w-100 h-100 d-flex justify-content-center align-items-center" style="overflow: auto;">
                <img
                  :src="annotationSourceUrl"
                  alt="Annotated preview"
                  class="img-fluid"
                  style="object-fit: contain; max-height: 100%; max-width: 100%;"
                  crossorigin="anonymous"
                >
              </div>
            </div>

            <div v-if="isCropping" class="btn-group w-100 my-1" role="group" aria-label="Crop aspect ratio">
              <button
                type="button"
                class="btn btn-lg flex-fill"
                :class="activeAspect === 'free' ? 'btn-primary' : 'btn-outline-primary'"
                :disabled="uploading"
                @click.prevent="setAspectRatio('free')"
              >
                Free
              </button>
              <button
                type="button"
                class="btn btn-lg flex-fill"
                :class="activeAspect === 'square' ? 'btn-primary' : 'btn-outline-primary'"
                :disabled="uploading"
                @click.prevent="setAspectRatio('square')"
              >
                1:1
              </button>
              <button
                type="button"
                class="btn btn-lg flex-fill"
                :class="activeAspect === 'landscape' ? 'btn-primary' : 'btn-outline-primary'"
                :disabled="uploading"
                @click.prevent="setAspectRatio('landscape')"
              >
                4:3
              </button>
            </div>


            <div v-if="uploading" class="progress mt-3" style="height: 4px;">
              <div
                class="progress-bar"
                role="progressbar"
                :style="{ width: uploadProgress + '%' }"
                :aria-valuenow="uploadProgress"
                aria-valuemin="0"
                aria-valuemax="100"
              ></div>
            </div>

            <p v-if="uploadError" class="text-danger small mt-2 mb-0">{{ uploadError }}</p>
          </div>

          <div class="modal-footer d-flex">
            <div class="col d-flex gap-3">
              <button type="button" class="btn btn-danger btn-lg" :disabled="uploading" @click.prevent="cancelCrop">
                Cancel
              </button>
            </div>

            <div v-if="isCropping" class="col-auto">
              <button type="button" class="btn btn-primary btn-lg" :disabled="uploading" @click.prevent="cropAndAnnotate">
                Crop
              </button>
            </div>

            <div v-if="isAnnotating" class="col-auto d-flex gap-3">
              <button
                v-if="!annotationEditorInstance"
                type="button"
                class="btn btn-outline-primary px-3"
                :disabled="uploading"
                @click.prevent="openMarkupEditor"
              >
                <span class="fa fa-solid fa-pen-to-square"></span>
              </button>
              <button type="button" class="px-3 btn btn-lg btn-primary" :disabled="uploading || annotationEditorInstance || (!annotatedFile && !croppedFileForAnnotation)" @click.prevent="finishWorkflow">
                <span class="">Finish</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.modal-body {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  min-height: 60vh;
  overflow: hidden;
}

.step-image-cropper__canvas {
  flex-grow: 1;
  overflow: hidden;
}
</style>
